import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { Mic2, Youtube, Music4, ExternalLink, Download, Loader2, MapPin, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_HEX = { violet: "#8a2be2", gold: "#d4af37", red: "#c82828", blue: "#3c5adc", green: "#28a05a" };

export default function PressKitPage({ slug, onBack }) {
    const { lang } = useLang();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/artists/${slug}/presskit`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setData)
            .catch(() => setErr(true));
    }, [slug]);

    if (err) {
        return (
            <section className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-cream">
                <p className="font-mono text-[10px] tracking-[0.3em] text-red-400">{lang === "fr" ? "PRESS KIT INTROUVABLE" : "PRESS KIT NOT FOUND"}</p>
                <button onClick={onBack} className="mt-6 flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-cream/70 hover:border-cream hover:text-cream">
                    <ArrowLeft size={13} /> {lang === "fr" ? "RETOUR" : "BACK"}
                </button>
            </section>
        );
    }
    if (!data) {
        return (
            <section className="flex min-h-screen items-center justify-center bg-ink text-cream">
                <Loader2 size={22} className="animate-spin text-gold" />
            </section>
        );
    }

    const pk = data.presskit || {};
    const c1 = COLOR_HEX[pk.color1] || COLOR_HEX.violet;
    const c2 = COLOR_HEX[pk.color2] || COLOR_HEX.gold;
    const links = data.links || {};

    return (
        <section data-testid="presskit-page" className="relative min-h-screen bg-ink pb-24 text-cream">
            {/* bandeau couleur */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[50vh]" style={{ background: `radial-gradient(70% 60% at 50% 0%, ${c1}40 0%, transparent 70%)` }} />
            <div className="relative mx-auto max-w-[1100px] px-5 pt-28 md:px-10">
                <button data-testid="presskit-back" onClick={onBack} className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-cream/50 transition-colors hover:text-cream">
                    <ArrowLeft size={12} /> {lang === "fr" ? "RETOUR" : "BACK"}
                </button>
                <SectionLabel en="PRESS KIT" fr="PRESS KIT" />
                <FadeUp delay={0.05}>
                    <div className="mt-8 flex flex-col items-center text-center">
                        <div className="h-36 w-36 overflow-hidden rounded-full border-4" style={{ borderColor: c2 }}>
                            {data.photo ? (
                                <img data-testid="presskit-photo" src={data.photo.startsWith("/") ? `${API}${data.photo}` : data.photo} alt={data.artist} className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center bg-white/5"><Mic2 size={38} style={{ color: c2 }} /></div>
                            )}
                        </div>
                        <h1 data-testid="presskit-name" className="mt-6 font-display text-[clamp(2.6rem,8vw,5.5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                            {data.artist}
                        </h1>
                        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                            {pk.style && (
                                <span data-testid="presskit-style" className="border px-4 py-1.5 font-mono text-[9px] tracking-[0.25em]" style={{ borderColor: `${c2}80`, color: c2 }}>
                                    {pk.style.toUpperCase()}
                                </span>
                            )}
                            {pk.city && (
                                <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.25em] text-cream/50">
                                    <MapPin size={11} /> {pk.city.toUpperCase()}
                                </span>
                            )}
                        </div>
                    </div>
                </FadeUp>

                {pk.bio && (
                    <FadeUp delay={0.12}>
                        <p data-testid="presskit-bio" className="mx-auto mt-8 max-w-2xl text-center font-serif text-sm italic leading-relaxed text-cream/75">
                            {pk.bio}
                        </p>
                    </FadeUp>
                )}

                {/* réseaux */}
                <FadeUp delay={0.16}>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        {links.youtube && (
                            <a data-testid="presskit-youtube" href={links.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-red-500/50 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-red-400 transition-all hover:bg-red-500 hover:text-cream">
                                <Youtube size={13} /> YOUTUBE
                            </a>
                        )}
                        {links.spotify && (
                            <a data-testid="presskit-spotify" href={links.spotify} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-green-500/50 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-green-400 transition-all hover:bg-green-500 hover:text-cream">
                                <Music4 size={13} /> SPOTIFY
                            </a>
                        )}
                        {links.instagram && (
                            <a data-testid="presskit-instagram" href={links.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-fuchsia-500/50 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-fuchsia-400 transition-all hover:bg-fuchsia-500 hover:text-cream">
                                <ExternalLink size={13} /> INSTAGRAM
                            </a>
                        )}
                    </div>
                </FadeUp>

                {/* photos presse */}
                {(pk.photos || []).length > 0 && (
                    <FadeUp delay={0.2}>
                        <div className="mt-12">
                            <p className="text-center font-mono text-[9px] tracking-[0.35em]" style={{ color: c2 }}>
                                {lang === "fr" ? "PHOTOS PRESSE — CLIC POUR TÉLÉCHARGER" : "PRESS PHOTOS — CLICK TO DOWNLOAD"}
                            </p>
                            <div data-testid="presskit-photos" className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                                {pk.photos.map((name) => (
                                    <a key={name} data-testid={`presskit-photo-${name}`} href={`${API}/api/uploads/offers/${name}`} download={name} target="_blank" rel="noreferrer"
                                        className="group relative block aspect-square overflow-hidden border border-white/10">
                                        <img src={`${API}/api/uploads/offers/${name}`} alt={data.artist} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <span className="absolute inset-0 flex items-center justify-center bg-ink/50 opacity-0 transition-opacity group-hover:opacity-100">
                                            <Download size={18} className="text-cream" />
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </FadeUp>
                )}

                {/* clips */}
                {(data.clips || []).length > 0 && (
                    <FadeUp delay={0.24}>
                        <div className="mt-12">
                            <p className="text-center font-mono text-[9px] tracking-[0.35em]" style={{ color: c2 }}>CLIPS GLOOM</p>
                            <div className="mt-6 grid gap-4 md:grid-cols-2">
                                {data.clips.map((clip) => (
                                    <motion.a
                                        key={clip.id}
                                        data-testid={`presskit-clip-${clip.id}`}
                                        href={`https://youtu.be/${clip.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        className="group block overflow-hidden border border-white/10"
                                    >
                                        <div className="relative aspect-video overflow-hidden">
                                            <img src={`https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`} alt={clip.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        </div>
                                        <p className="p-3 font-mono text-[9px] tracking-[0.2em] text-cream/70">{clip.title}</p>
                                    </motion.a>
                                ))}
                            </div>
                        </div>
                    </FadeUp>
                )}

                <p className="mt-14 text-center font-mono text-[8px] tracking-[0.3em] text-cream/30">
                    GLOOM ENT. — {lang === "fr" ? "PRESS KIT OFFICIEL" : "OFFICIAL PRESS KIT"}
                </p>
            </div>
        </section>
    );
}
